import CalculatedDebtResult from "../Types/CalculatedDebtResult";
import { PaymentType } from "../Types/Enums/PaymentType";

export default class TipCalculationService {

    // Valid currency values
    private ValidCurrencyValues = [50, 20, 10, 5, 2, 1, 0.50, 0.20, 0.10, 0.05];

    public SetValidCurrencyValues(values: number[]) {
        if (!values || values.length <= 0) return;

        const cleaned = Array.from(new Set(values.filter(v => Number.isFinite(v) && v > 0)))
            .sort((a, b) => b - a);

        this.ValidCurrencyValues = cleaned;
        this.NotifyOfPropertyChange();
    }

    public GetValidCurrencyValues(): number[] {
        return this.ValidCurrencyValues;
    }


    // Payment type
    private PaymentType = PaymentType.NotSpecified;

    public SetPaymentType(type: PaymentType) {
        if (type === PaymentType.NotSpecified) return;
        if (type === this.PaymentType) return;

        this.PaymentType = type;
        this.NotifyOfPropertyChange();
    }

    public GetPaymentType(): PaymentType {
        return this.PaymentType;
    }


    // Exact calculation
    private ExactCalculation = true;

    public SetExactCalculation(value: boolean) {
        if (value === this.ExactCalculation) return;

        this.ExactCalculation = value;
        this.NotifyOfPropertyChange();
    }

    public GetExactCalculation(): boolean {
        return this.ExactCalculation;
    }


    // PropertyChangeListeners
    private PropertyChangedListeners: Array<() => void> = [];

    private NotifyOfPropertyChange() {
        this.PropertyChangedListeners.forEach(x => x());
    }

    public AddPropertyChangedListener(value: () => void) {
        this.PropertyChangedListeners.push(value);
    }

    public RemovePropertyChangedListener(value: () => void) {
        const i = this.PropertyChangedListeners.indexOf(value);
        if (i >= 0) this.PropertyChangedListeners.splice(i, 1);
    }


    // Helpers
    private readonly EPS = 1e-9;
    private round2 = (x: number) => Math.round((x + this.EPS) * 100) / 100;

    private pickStep(totalTip: number): number {
        if (totalTip <= 5) return 0.5;
        if (totalTip <= 10) return 1;
        if (totalTip <= 30) return 2;
        if (totalTip <= 150) return 5;
        return 10;
    }


    // A negative debt means the worker has to give money back
    // A positive debt means the worker has money to receive
    public CalculateTip(cashTip: number, pinTip: number, openDebt?: number): CalculatedDebtResult {
        const totalTip = (cashTip || 0) + (pinTip || 0) + (openDebt || 0);

        if (this.ExactCalculation) {
            return this.CalculateExactTip(totalTip);
        } else {
            return this.CalculateRoundedTip(totalTip);
        }
    }

    // If the calculation is for a rounded tip, you should pass the original values as well for returning totalTip
    private CalculateExactTip(totalTip: number, originalTip?: number): CalculatedDebtResult {
        // Work on the magnitude so the greedy denomination fit is correct, then
        // re-apply the sign. Without this, a negative total (worker gives money
        // back) produced an all-zero currency map because Math.trunc of a
        // negative quotient rounds toward zero.
        const sign = totalTip < 0 ? -1 : 1;
        const currentCurrencies: Record<number, number> = {};
        let currentTip = Math.abs(totalTip);

        for (const currency of this.ValidCurrencyValues) {
            const numOfCurrency = Math.trunc((currentTip + this.EPS) / currency);
            currentCurrencies[currency] = sign * numOfCurrency;
            currentTip -= numOfCurrency * currency;
        }

        const fittedTip = this.round2(
            Object.entries(currentCurrencies)
                .reduce((acc, [cur, num]) => acc + Number(cur) * (num as number), 0)
        );

        const original = originalTip ?? totalTip;
        const result: CalculatedDebtResult = {
            totalTip: this.round2(original),
            roundedTip: fittedTip,
            debt: this.round2(original - fittedTip),
            currency: currentCurrencies,
            paymentType: this.PaymentType
        };

        return result;
    }

    private CalculateRoundedTip(totalTip: number): CalculatedDebtResult {
        if (!Number.isFinite(totalTip)) return {
            totalTip: totalTip,
            roundedTip: totalTip,
            currency: {},
            debt: 0,
            paymentType: this.PaymentType,
        };

        // Round the magnitude and re-apply the sign afterwards. A negative total
        // (the worker owes money back) must still be rounded to a payable amount
        // instead of being passed through untouched with a debt of 0.
        const sign = totalTip < 0 ? -1 : 1;
        const magnitude = Math.abs(totalTip);
        const step = this.pickStep(magnitude);

        const floorCandidate = Math.floor((magnitude + this.EPS) / step) * step;
        const baseCandidate = Math.round((magnitude + this.EPS) / step) * step;
        const ceilCandidate = Math.ceil((magnitude + this.EPS) / step) * step;

        const pieceCount = (amount: number): number => {
            let remaining = amount;
            let pieces = 0;
            for (const d of this.ValidCurrencyValues) {
                const count = Math.floor((remaining + this.EPS) / d);
                pieces += count;
                remaining -= count * d;
            }
            return pieces;
        };

        const candidates = [floorCandidate, baseCandidate, ceilCandidate];

        let best = candidates[0];
        let bestPieces = pieceCount(best);
        let bestDebt = Math.abs(magnitude - best);

        for (const c of candidates) {
            const pieces = pieceCount(c);
            const debt = Math.abs(magnitude - c);

            if (
                pieces < bestPieces ||
                (pieces === bestPieces && (debt < bestDebt || (debt === bestDebt && c > best)))
            ) {
                best = c;
                bestPieces = pieces;
                bestDebt = debt;
            }
        }

        return this.CalculateExactTip(this.round2(sign * best), totalTip);
    }
}
